import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { httpService } from '@/api/v1/http.service'
import type { IdAndName } from '@/types/id-and-name'
import type { PaginationParams } from '@/types/params'
import { requiredString } from '@/utils/zod'

const responseSchema = z.array(
  z.object({
    id: requiredString(),
    display_name: requiredString(),
    academic_level: z
      .object({
        id: requiredString(),
        name: requiredString()
      })
      .nullable(),
    tutor: z
      .object({
        id: requiredString(),
        full_name: requiredString(),
        first_name: requiredString(),
        last_name: requiredString()
      })
      .nullable()
      .optional(),
    number_of_students: z.number()
  })
)

export type Class = {
  id: string
  name: string
  academicLevel?: IdAndName
  tutor?: {
    id: string
    fullName: string
  }
  numberOfStudents: number
}

const parseResponse = (response: z.infer<typeof responseSchema>): Class[] =>
  response.map(item => ({
    id: item.id,
    name: item.display_name,
    academicLevel: item.academic_level || undefined,
    tutor: item.tutor
      ? {
          id: item.tutor?.id,
          fullName: item.tutor?.full_name
        }
      : undefined,
    numberOfStudents: item.number_of_students
  }))

export type StudentClassesParams = {
  id?: string[]
  academicLevelId?: string[]
  tutorId?: string[]
  studentId?: string[]
} & PaginationParams

const useStudentClasses = (params: StudentClassesParams = {}) =>
  useQuery({
    queryKey: ['panelClassesList', params],
    staleTime: 60 * 100,
    queryFn: async () => {
      const response = await httpService.panel.panelClassesList({
        id: params.id,
        page: params.page,
        // @ts-expect-error Waits for backend fix
        page_size: params.pageSize,
        student_id: params.studentId,
        tutor_id: params.tutorId,
        academic_level_id: params.academicLevelId,
        fetchKeys: {
          id: true,
          display_name: true,
          tutor: true,
          academic_level: true,
          number_of_students: true
        }
      })

      const parsed = parseResponse(responseSchema.parse(response.results))
      return {
        list: parsed,
        count: response.count
      }
    }
  })

export default useStudentClasses
